import {
  Intent,
  MenuItem,
  PopoverInteractionKind,
  Position,
} from '@blueprintjs/core';
import { ItemRendererProps, Suggest } from '@blueprintjs/select';
import * as React from 'react';

import { BooleanInput } from 'bones/components/atoms/inputs/BooleanInput';
import { NumericInput } from 'bones/components/atoms/inputs/NumericInput';
import { StringInput } from 'bones/components/atoms/inputs/StringInput';
import { Tooltip } from 'bones/components/atoms/Tooltip';
import {
  GenericPropertiesRowValidation,
  GenericPropertiesValidator,
  GenericProperty,
  GenericPropertySuggestion,
  GenericPropertySuggestionType,
} from 'bones/components/molecules/GenericPropertiesEditor/types';
import { XSD_DECIMAL_IRI, XSD_INTEGER_IRI } from 'bones/constants/iris';
import { alphaSort } from 'bones/utils/sort/alphaSort';

interface GenericPropertySuggestionWithTestId
  extends GenericPropertySuggestion {
  itemTestId: string | void;
}

const PropertyNameSuggest =
  // eslint-disable-next-line @blueprintjs/no-deprecated-components
  Suggest.ofType<GenericPropertySuggestionWithTestId>();

const itemRenderer = (
  item: GenericPropertySuggestionWithTestId,
  options: ItemRendererProps
) => {
  const { handleClick, modifiers } = options;
  const { name, itemTestId } = item;

  return (
    <MenuItem
      key={name}
      active={modifiers.active}
      data-testid={itemTestId}
      disabled={modifiers.disabled}
      onClick={handleClick}
      text={name}
    />
  );
};

export interface PropertiesRowProps {
  actionElementRenderer: (
    disabled: boolean,
    tooltipContent: string,
    intent: Intent
  ) => JSX.Element | null;
  disabled?: boolean;
  isNewRow: boolean;
  propertySuggestions?: GenericPropertySuggestion[];
  name: string;
  maxSuggestions?: number;
  properties: GenericProperty[];
  propertyNameTestId?: string;
  propertyValueTestId?: string;
  suggestionItemTestId?: (name: string, index?: number) => string | void;
  testId: string;
  updateProperty: (name: string, value: string) => void;
  usePortal?: boolean;
  validator?: GenericPropertiesValidator;
  value: string;
}

export const GenericPropertiesRow: React.VFC<PropertiesRowProps> = ({
  actionElementRenderer,
  disabled,
  isNewRow,
  propertySuggestions = [],
  name,
  maxSuggestions,
  properties,
  propertyNameTestId,
  propertyValueTestId,
  suggestionItemTestId,
  testId,
  updateProperty,
  usePortal,
  validator = () => ({} as GenericPropertiesRowValidation),
  value,
}) => {
  const validation = validator(isNewRow, name, value);

  const propertySuggestionsWithTestIds = React.useMemo(
    () =>
      propertySuggestions
        .slice(0, maxSuggestions || propertySuggestions.length)
        .map((suggestion, index) => ({
          ...suggestion,
          itemTestId: suggestionItemTestId?.(suggestion.name, index),
        })),
    [maxSuggestions, propertySuggestions, suggestionItemTestId]
  );

  const knownProperty = React.useMemo(() => {
    const index = propertySuggestions.findIndex(
      ({ name: knownValidName }) => name === knownValidName
    );
    if (index === -1) {
      return null;
    }
    return {
      ...propertySuggestions[index],
      itemTestId: suggestionItemTestId?.(
        propertySuggestions[index].name,
        index
      ),
    };
  }, [name, propertySuggestions, suggestionItemTestId]);

  const nameItemListPredicate = (
    query: string,
    items: GenericPropertySuggestionWithTestId[]
  ) =>
    items
      .filter(
        (knownValidItem) =>
          knownValidItem.name.includes(query) &&
          !properties.some(
            (existingProperty) => existingProperty.name === knownValidItem.name
          )
      )
      .sort(alphaSort(({ name }) => name));

  const nameElement = (
    <PropertyNameSuggest
      disabled={disabled}
      fill
      inputProps={
        {
          'data-testid': propertyNameTestId,
          intent: validation?.name?.inputIntent || Intent.NONE,
          placeholder: '',
        } as any
      }
      inputValueRenderer={({ name }) => name}
      itemListPredicate={nameItemListPredicate}
      itemRenderer={itemRenderer}
      items={propertySuggestionsWithTestIds}
      onItemSelect={(item: GenericPropertySuggestion) => {
        updateProperty(item.name, item.defaultValue || '');
      }}
      onQueryChange={(newName: string) => {
        const newKnownProperty =
          propertySuggestions.find(
            ({ name: knownValidName }) => newName === knownValidName
          ) || null;
        // Only override the value if the name is for a property
        // that is not of the same type, and a defaultValue is
        // specified
        const currentValueType =
          knownProperty?.type || GenericPropertySuggestionType.STRING;
        const newValue =
          newKnownProperty &&
          newKnownProperty.type !== currentValueType &&
          newKnownProperty.defaultValue !== undefined
            ? newKnownProperty.defaultValue
            : value;
        updateProperty(newName, newValue);
      }}
      popoverProps={{
        disabled: propertySuggestions.length === 0,
        minimal: true,
        usePortal,
        popoverClassName: 'sd-popover-menu',
        portalContainer:
          (process.env.NODE_ENV === 'test' &&
            document.getElementById('vet-bones-root')) ||
          // istanbul ignore next
          undefined,
      }}
      query={name}
      resetOnQuery={false}
      selectedItem={
        knownProperty || {
          name,
          type: GenericPropertySuggestionType.STRING,
          itemTestId: suggestionItemTestId?.(name),
        }
      }
    />
  );

  let valueElement;
  switch (knownProperty?.type) {
    case GenericPropertySuggestionType.BOOLEAN:
      valueElement = (
        <BooleanInput
          disabled={disabled}
          inputTestId={propertyValueTestId}
          label={value}
          large
          onSetValue={(newValue) => updateProperty(name, newValue)}
          value={value}
        />
      );
      break;
    case GenericPropertySuggestionType.INTEGER:
      valueElement = (
        <NumericInput
          disabled={disabled}
          inputTestId={propertyValueTestId}
          intent={validation?.value?.inputIntent || Intent.NONE}
          onSetValue={(newValue) => updateProperty(name, newValue)}
          placeholder={knownProperty?.defaultValue || ''}
          range={XSD_INTEGER_IRI}
          value={value}
        />
      );
      break;
    case GenericPropertySuggestionType.DECIMAL:
      valueElement = (
        <NumericInput
          disabled={disabled}
          inputTestId={propertyValueTestId}
          intent={validation?.value?.inputIntent || Intent.NONE}
          onSetValue={(newValue) => updateProperty(name, newValue)}
          placeholder={knownProperty?.defaultValue || ''}
          range={XSD_DECIMAL_IRI}
          value={value}
        />
      );
      break;
    case GenericPropertySuggestionType.STRING:
    default:
      valueElement = (
        <StringInput
          disabled={disabled}
          inputTestId={propertyValueTestId}
          intent={validation?.value?.inputIntent || Intent.NONE}
          onSetValue={(newValue) => updateProperty(name, newValue)}
          placeholder={knownProperty?.defaultValue || ''}
          value={value}
        />
      );
  }
  const actionTooltipContent = validation?.actionTooltip || '';
  const disableAction = disabled || validation?.disableAction || false;
  const actionElementIntent = validation?.actionIntent || Intent.NONE;
  const [isNameTooltipHovered, setIsNameTooltipHovered] = React.useState(false);
  const [isValueTooltipHovered, setIsValueTooltipHovered] =
    React.useState(false);

  return (
    <tr className="sd-generic-properties-editor__row" data-testid={testId}>
      <td
        aria-hidden="true"
        className="sd-generic-properties-editor__hidden-tooltip-cell"
      >
        <Tooltip
          content={knownProperty?.description || ''}
          disabled={!knownProperty?.description}
          interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}
        >
          <div className="sd-generic-properties-editor__hidden-tooltip-cell__background" />
        </Tooltip>
      </td>
      <td
        className="sd-generic-properties-editor__field"
        onMouseEnter={() => setIsNameTooltipHovered(true)}
        onMouseLeave={() => setIsNameTooltipHovered(false)}
      >
        <Tooltip
          content={validation?.name?.inputTooltip || ''}
          disabled={!validation?.name?.inputTooltip}
          fill
          interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}
          isOpen={isNameTooltipHovered}
          placement={Position.TOP}
          usePortal={false}
        />
        {nameElement}
      </td>
      <td
        className="sd-generic-properties-editor__field"
        onMouseEnter={() => setIsValueTooltipHovered(true)}
        onMouseLeave={() => setIsValueTooltipHovered(false)}
      >
        <Tooltip
          content={validation?.value?.inputTooltip || ''}
          disabled={!validation?.value?.inputTooltip}
          fill
          interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}
          isOpen={isValueTooltipHovered}
          placement={Position.TOP}
          usePortal={false}
        />
        {valueElement}
      </td>
      <td>
        {actionElementRenderer(
          disableAction,
          actionTooltipContent,
          actionElementIntent
        )}
      </td>
    </tr>
  );
};
